import React, { useState, useEffect } from 'react';
import { Bounce, toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';

import { decodeFromHex } from '../helper/commonHelper';
import { transactionAction } from '../helper/chain-helper';
import { getHostContract, getHostHelperContract } from '../helper/contract-helper';

import { logo } from '../assets/svg/svg';

import Loader from '../components/Loader';
import './ConfirmSendAs.css';
import { getSendAsEmails } from '../helper/settingsHelper';
import { CompleteLoader } from '../modals/common-alert';
import { reportError, toErrorString } from '../utils/errorLogger';

const ConfirmSendAs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const cookies = new Cookies();
  const userData = cookies.get('userObject');

  const refreshSettings = useSelector((state) => state.refreshSettings);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [validatedEmail, setValidatedEmail] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [sendLoader, setSendLoader] = useState(false);

  useEffect(() => {
    const validate = async () => {
      // 1. Auth check
      if (!userData) {
        navigate('/');
        return;
      }

      // 2. Get and decode token
      const token = new URLSearchParams(location.search).get('token');
      if (!token) {
        setError('Invalid URL: Missing token.');
        setLoading(false);
        return;
      }

      const decodedEmail = decodeFromHex(token);
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!decodedEmail || !emailRegex.test(decodedEmail)) {
        setError('Invalid URL: Malformed token.');
        setLoading(false);
        return;
      }

      // 3. Check if user exists on-chain
      try {
        const hostContract = getHostContract();
        const publicKey = await hostContract.methods.getPublicKeyOfUser(decodedEmail).call();
        if (!publicKey) {
          setError('Invalid URL: User not found.');
          setLoading(false);
          return;
        }
      } catch (e) {
        const userName = localStorage.getItem('UserName');
        const errorStr = toErrorString(e || e.message);
        reportError({
          endpoint: '-',
          message: `FileName: confirmSendAs.js, Fun: updateSendAsEmails, ${String(e.message || 'Unhandled error')}`,
          error: errorStr,
          userName: userName || '',
        });
        console.error("Error validating user:", e);
        setError('An error occurred during validation.');
        setLoading(false);
        return;
      }

      // All checks passed
      setValidatedEmail(decodedEmail);
      setLoading(false);
    };

    validate();
  }, [userData, location.search, navigate]);

  const updateSendAsEmails = async (newEmails) => {
    setSendLoader(true);
    try {
      const options = [];
      newEmails.forEach((email, index) => {
        options.push(
          { key: `email~${index + 1}`, value: email.email },
          { key: `isDefault~${index + 1}`, value: email.isDefault === true || email.isDefault === 'true' ? "true" : "false" },
          { key: `verify~${index + 1}`, value: email.verify === true || email.verify === 'true' ? "true" : "false" }
        );
      });
      const helperContract = getHostHelperContract();
      const functionParams = [validatedEmail, 'sendAsEmail', options];
      const hash = await transactionAction(helperContract, "updateSettings", functionParams, '', 'helper');
      if (hash) {
        dispatch({ type: "REFRESH_SETTINGS", payload: !refreshSettings });
        toast.success("Successfully verified.", {
          position: "top-center",
          transition: Bounce,
        });
        setIsVerified(true)
        setSendLoader(false);
      } else {
        toast.error("Something went wrong.", {
          position: "top-center",
          transition: Bounce,
        });
        setSendLoader(false);
      }
    } catch (error) {
      const userName = localStorage.getItem('UserName');
      const errorStr = toErrorString(error || error.message);
      reportError({
        endpoint: '-',
        message: `FileName: confirmSendAs.js, Fun: validate, ${String(error.message || 'Unhandled error')}`,
        error: errorStr,
        userName: userName || '',
      });
      toast.error("Something went wrong.", {
        position: "top-center",
        transition: Bounce,
      });
      setLoading(false);
    }
  };


  const handleConfirm = async () => {
    // Logic to confirm the email
    console.log('Confirmed email:', validatedEmail);
    const sendAsEmail = await getSendAsEmails(validatedEmail);
    const index = sendAsEmail.findIndex((email) => email?.email == userData?.name);
    sendAsEmail[index].verify = true;
    await updateSendAsEmails(sendAsEmail);
  };

  if (loading) {
    return (
      <div className="confirm-sendas-bg min-vh-100 d-flex flex-column align-items-center justify-content-center">
        <Loader normal={true} />
        <div>Validating...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="confirm-sendas-bg min-vh-100 d-flex flex-column align-items-center justify-content-center">
        <div className="confirm-sendas-card p-5 rounded-4 shadow-lg bg-white text-center">
          <div className="mb-3">{logo.dmail_logo_svg}</div>
          <h3 className="mb-2 text-danger">Validation Failed</h3>
          <p className="mb-4">{error}</p>
          <a href="/" className="text-decoration-underline">Return to Dmail Home</a>
        </div>
      </div>
    );
  }

  return (
    <div className="confirm-sendas-bg min-vh-100 d-flex flex-column align-items-center justify-content-center">
      <div className="confirm-sendas-card p-5 rounded-4 shadow-lg bg-white text-center full-width-max-500">
        <div className="mb-3">
          {logo.dmail_logo_svg}
        </div>
        <h3 className="mb-2 fill-131717">Dmail Confirmation</h3>
        <div className="mb-3 text-gray-medium">Secure your communication with Dmail</div>
        <p className="mb-2 fs-5 fill-36494d" >Please confirm mail sending as <span className="fw-semibold text-primary text-20px">{userData?.name}</span>.</p>
        <p className="mb-4 fill-a3abad">Click below to confirm and return to your Dmail account.</p>
        {!isVerified ? <button className="btn btn-gradient px-4 py-2 fw-semibold mb-3" onClick={handleConfirm}>
          Confirm
        </button>
          : <span className="btn btn-gradient px-4 py-2 fw-semibold mb-3"> Verified </span>
        }
        <div>
          <a href="/" className="text-decoration-underline">Return to Dmail Home</a>
        </div>
      </div>
      <footer className="mt-5 text-center small">
        &copy; {new Date().getFullYear()} Dmail &ndash; <a href="/privacy" className="text-decoration-underline">Privacy Policy</a>
      </footer>
      <CompleteLoader isOpen={sendLoader} text={"Verifying..."} />
    </div>
  );
};

export default ConfirmSendAs;